import React, { useState } from 'react';
import { X, Brain, Sparkles } from 'lucide-react';
import { aiDecompositionService } from '../services/aiDecompositionService';
import { AIDecompositionResponse } from '../types/ai';
import { AIResultsDisplay } from './AI/AIResultsDisplay';

interface AIDecomposerProps {
  projectId: string;
  onClose: () => void;
  onComplete: () => void;
}

const AIDecomposer: React.FC<AIDecomposerProps> = ({ projectId, onClose, onComplete }) => {
  const [step, setStep] = useState<'input' | 'processing' | 'results'>('input');
  const [formData, setFormData] = useState({
    description: '',
    projectType: 'software',
    techStack: [] as string[],
    architecture: '',
    existingAssets: [] as string[],
    budget: '',
    timeline: '',
    businessModel: '',
    targetAudience: ''
  });
  const [techStackInput, setTechStackInput] = useState('');
  const [assetsInput, setAssetsInput] = useState('');
  const [decomposition, setDecomposition] = useState<AIDecompositionResponse | null>(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('Project description is required');
      return;
    }

    setError('');
    setStep('processing');

    try {
      const response = await aiDecompositionService.decomposeProject(projectId, {
        description: formData.description,
        projectType: formData.projectType,
        techStack: formData.techStack,
        architecture: formData.architecture || undefined,
        existingAssets: formData.existingAssets,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        timeline: formData.timeline || undefined,
        businessModel: formData.businessModel || undefined,
        targetAudience: formData.targetAudience || undefined
      });

      setDecomposition(response);
      setStep('results');
    } catch (err) {
      console.error('AI Decomposition failed:', err);
      setError('AI processing failed. Please try again.');
      setStep('input');
    }
  };

  const handleAccept = async () => {
    if (!decomposition?.id) return;

    setAccepting(true);
    try {
      await aiDecompositionService.acceptDecomposition(decomposition.id);
      onComplete();
    } catch (err) {
      console.error('Failed to accept decomposition:', err);
      setError('Failed to create goals. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const addTechStack = () => {
    if (techStackInput.trim() && !formData.techStack.includes(techStackInput.trim())) {
      setFormData({
        ...formData,
        techStack: [...formData.techStack, techStackInput.trim()]
      });
      setTechStackInput('');
    }
  };

  const removeTechStack = (tech: string) => {
    setFormData({
      ...formData,
      techStack: formData.techStack.filter(t => t !== tech)
    });
  };

  const addAsset = () => {
    if (assetsInput.trim() && !formData.existingAssets.includes(assetsInput.trim())) {
      setFormData({
        ...formData,
        existingAssets: [...formData.existingAssets, assetsInput.trim()]
      });
      setAssetsInput('');
    }
  };

  const removeAsset = (asset: string) => {
    setFormData({
      ...formData,
      existingAssets: formData.existingAssets.filter(a => a !== asset)
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-900">AI Project Decomposer</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {step === 'input' && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="text-center mb-6">
                <Sparkles className="mx-auto w-12 h-12 text-purple-500 mb-3" />
                <p className="text-gray-600">
                  Describe your project and let AI create the perfect goal structure and implementation plan
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field resize-none"
                  placeholder="Describe your project: features, objectives, target users, key functionalities..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Type
                  </label>
                  <select
                    value={formData.projectType}
                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                    className="input-field"
                  >
                    <option value="software">Software Development</option>
                    <option value="marketing">Marketing Campaign</option>
                    <option value="content">Content Creation</option>
                    <option value="research">Research Project</option>
                    <option value="design">Design Project</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Architecture
                  </label>
                  <input
                    type="text"
                    value={formData.architecture}
                    onChange={(e) => setFormData({ ...formData, architecture: e.target.value })}
                    className="input-field"
                    placeholder="e.g., microservices, monolith, serverless"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tech Stack
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={techStackInput}
                    onChange={(e) => setTechStackInput(e.target.value)}
                    className="input-field flex-1"
                    placeholder="Add technology (e.g., React, Node.js, PostgreSQL)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTechStack())}
                  />
                  <button
                    type="button"
                    onClick={addTechStack}
                    className="btn-secondary"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.techStack.map((tech, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                    >
                      {tech}
                      <button
                        type="button"
                        onClick={() => removeTechStack(tech)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Existing Assets
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={assetsInput}
                    onChange={(e) => setAssetsInput(e.target.value)}
                    className="input-field flex-1"
                    placeholder="Add existing asset (e.g., User Auth System 70%, Payment Gateway Done)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAsset())}
                  />
                  <button
                    type="button"
                    onClick={addAsset}
                    className="btn-secondary"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.existingAssets.map((asset, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full"
                    >
                      {asset}
                      <button
                        type="button"
                        onClick={() => removeAsset(asset)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget
                  </label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="input-field"
                    placeholder="Total budget in USD"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeline
                  </label>
                  <input
                    type="text"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    className="input-field"
                    placeholder="e.g., 6 months, Q4 2024"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Model
                  </label>
                  <input
                    type="text"
                    value={formData.businessModel}
                    onChange={(e) => setFormData({ ...formData, businessModel: e.target.value })}
                    className="input-field"
                    placeholder="e.g., SaaS, marketplace, B2B"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    className="input-field"
                    placeholder="e.g., developers, small businesses"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Generate Project Structure
                </button>
              </div>
            </form>
          )}

          {step === 'processing' && (
            <div className="p-8 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">AI is analyzing your project</h3>
              <p className="text-gray-600">Creating goals, identifying opportunities, and analyzing dependencies...</p>
            </div>
          )}

          {step === 'results' && decomposition && (
            <AIResultsDisplay
              decomposition={decomposition}
              onAccept={handleAccept}
              onBack={() => setStep('input')}
              onClose={onClose}
              accepting={accepting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIDecomposer;
